// Third Party
import * as React from 'react';
import { Session } from 'next-auth';

// Utils
import { useLoginAndSignupModals } from '@contexts/use-login-and-signup-modals';
import { routes } from '@routes';
import { SearchPageContentType } from '@gql';

// UI
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import { signOut } from 'next-auth/react';
import { NewsletterForm } from '@components/newsletter-form';

type Props = {
  session: Session | null;
};

type Link = {
  href: string | VoidFunction;
  name: string;
  target?: string;
  rel?: string;
};

type MainFooterNav = {
  [key: string]: Array<{
    name: string;
    href: string | (() => void);
    rel?: string;
    target?: string;
  }>;
};

const authenticatedTravelerNav: MainFooterNav = {
  Account: [
    {
      name: 'Traveler Account',
      href: routes.userAccountSettings,
      rel: 'nofollow',
    },
    { name: 'Trips', href: routes.trips, rel: 'nofollow' },
    { name: 'Favorites', href: routes.userAccountFavorites, rel: 'nofollow' },
    { name: 'List your Properties', href: routes.list, rel: 'nofollow' },
    {
      name: 'Log out',
      href: () => signOut({ callbackUrl: '/', redirect: true }),
      rel: 'nofollow',
    },
  ],
  Company: [
    { name: 'About Us', href: routes.about },
    // { name: 'Careers', href: routes.careers, rel: 'nofollow' },
  ],
  Resources: [
    { name: 'Gift Cards', href: routes.giftCardPurchase },
    { name: 'Integrations', href: routes.integrations },
    // { name: 'Blog', href: routes.blog, rel: 'nofollow' },
  ],
  Support: [
    {
      name: 'Help Center',
      href: routes.help,
      target: '_blank',
      rel: 'nofollow',
    },
    { name: 'Accessibility', href: routes.accessibility, rel: 'nofollow' },
  ],
};

const authenticatedHostNav: MainFooterNav = {
  Account: [
    {
      name: 'Your Properties',
      href: routes.accountListings,
      rel: 'nofollow',
    },
    {
      name: 'Traveler Account',
      href: routes.userAccountSettings,
      rel: 'nofollow',
    },
    { name: 'Trips', href: routes.trips, rel: 'nofollow' },
    { name: 'Favorites', href: routes.userAccountFavorites, rel: 'nofollow' },
    {
      name: 'Log out',
      href: () => signOut({ callbackUrl: '/', redirect: true }),
      rel: 'nofollow',
    },
  ],
  Company: [
    { name: 'About Us', href: routes.about },
    // { name: 'Careers', href: routes.careers, rel: 'nofollow' },
  ],
  Resources: [
    { name: 'Gift Cards', href: routes.giftCardPurchase },
    { name: 'Integrations', href: routes.integrations },
    // { name: 'Blog', href: routes.blog, rel: 'nofollow' },
  ],
  Support: [
    {
      name: 'Help Center',
      href: routes.help,
      target: '_blank',
      rel: 'nofollow',
    },
    { name: 'Accessibility', href: routes.accessibility, rel: 'nofollow' },
  ],
};

const socialMediaLinks: Array<Link & { icon: IconNames }> = [
  {
    name: 'Facebook',
    href: routes.facebook,
    icon: IconNames.FACEBOOK,
    rel: 'nofollow',
    target: '_blank',
  },
  {
    name: 'Instagram',
    href: routes.instagram,
    icon: IconNames.INSTAGRAM,
    rel: 'nofollow',
    target: '_blank',
  },
  {
    name: 'LinkedIn',
    href: routes.linkedIn,
    icon: IconNames.LINKEDIN,
    rel: 'nofollow',
    target: '_blank',
  },
  {
    name: 'YouTube',
    href: routes.youtube,
    icon: IconNames.YOUTUBE,
    rel: 'nofollow',
    target: '_blank',
  },
  {
    name: 'X',
    href: routes.x,
    icon: IconNames.TWITTERX,
    rel: 'nofollow',
    target: '_blank',
  },
  {
    name: 'Pinterest',
    href: routes.pinterest,
    icon: IconNames.PINTEREST,
    rel: 'nofollow',
    target: '_blank',
  },
];

const bottomFooterLinks: Link[] = [
  { href: routes.privacy, name: 'Privacy', rel: 'nofollow' },
  { href: routes.terms, name: 'Terms', rel: 'nofollow' },
  {
    href: routes.sitemaps(SearchPageContentType.VacationRental),
    name: 'Site Map',
    rel: 'nofollow',
  },
];

const currentYear = new Date().getFullYear();

export const Footer: React.FC<Props> = ({ session }) => {
  const { activateLoginModal } = useLoginAndSignupModals();

  const unauthenticatedUserNav: MainFooterNav = {
    Account: [
      { name: 'Favorites', href: activateLoginModal },
      { name: 'Traveler Signup', href: routes.createAccount },
      { name: 'Log In', href: activateLoginModal },
      { name: 'List your Properties', href: routes.list },
    ],
    Company: [
      { name: 'About Us', href: routes.about },
      // { name: 'Careers', href: routes.careers },
    ],
    Resources: [
      { name: 'Gift Cards', href: routes.giftCardPurchase },
      { name: 'Integrations', href: routes.integrations },
      // { name: 'Blog', href: routes.blog },
    ],
    Support: [
      {
        name: 'Help Center',
        href: routes.help,
        target: '_blank',
        rel: 'nofollow',
      },
      { name: 'Accessibility', href: routes.accessibility, rel: 'nofollow' },
    ],
  };

  const mainFooterNav = () => {
    const userLoggedIn = session !== null && session?.user !== undefined;
    const belongsToAccount = session ? session?.user?.isHost : false;

    if (userLoggedIn && !belongsToAccount) {
      return authenticatedTravelerNav;
    } else if (userLoggedIn && belongsToAccount) {
      return authenticatedHostNav;
    } else {
      return unauthenticatedUserNav;
    }
  };

  return (
    <footer aria-labelledby="footer-heading" className="mt-6">
      <div id="footer-heading" className="sr-only">
        Footer
      </div>
      <div className="rounded-2xl bg-primary-dark pt-12 pb-[34px] px-12 text-secondary-xlight">
        <div className="grid grid-cols-1 gap-x-0 xl:gap-x-16 gap-y-12 lg:grid-cols-[300px_1fr]">
          <div className="flex space-x-4 text-white">
            <img
              src="/assets/svg/logos/savvy_wordmark_off_white.svg"
              className="block h-8 w-auto"
              alt="logo"
            />
            <p className="mt-1.5 text-lg">Stay smarter.</p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            {Object.keys(mainFooterNav()).map((key) => (
              <div key={key}>
                <h3 className="mb-5 text-lg font-medium">{key}</h3>
                <ul className="space-y-2">
                  {mainFooterNav()[key].map((item) => (
                    <li key={item.name}>
                      {typeof item.href === 'string' ? (
                        <Link
                          className="font-normal text-white no-underline transition-colors hover:text-secondary-xlight hover:underline focus:text-secondary-xlight active:text-secondary-light"
                          to={item.href}
                          rel={item.rel}
                          target={item.target}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <a
                          className="cursor-pointer text-white no-underline transition-colors hover:text-secondary-xlight hover:underline focus:text-secondary-xlight active:text-secondary-light"
                          onClick={item.href}
                          rel={item.rel}
                          target={item.target}
                        >
                          {item.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col pt-12 lg:flex-row lg:w-full lg:justify-between lg:items-end lg:pt-4">
          <div className="mb-[4px] max-w-80">
            <h3 className="mb-4 text-lg font-medium text-white">
              Stay in touch
            </h3>
            <div className="flex justify-between space-x-2 sm:space-x-4">
              {socialMediaLinks.map((item) => (
                <a
                  key={item.name}
                  href={typeof item.href === 'string' ? item.href : undefined}
                  target={item.target}
                  rel={item.rel}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-xlight transition-opacity hover:opacity-80 hover:cursor-pointer"
                >
                  <Icon
                    name={item.icon}
                    size="lg"
                    className="text-primary-main"
                  />
                </a>
              ))}
            </div>
          </div>
          <div className="mt-8">
            <NewsletterForm />
          </div>
        </div>
      </div>

      <div className="my-4 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-end">
        <p className="text-sm text-primary-main sm:mb-0">
          © {currentYear} finderco, LLC all rights reserved.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {bottomFooterLinks.map((item) => (
            <div key={item.name} className="flex items-center">
              {typeof item.href === 'string' && (
                <Link
                  to={item.href}
                  rel={item.rel}
                  className="text-sm hover:underline"
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
};
